// ══════════════════════════════════════════════════════════════
// COMPASS – Language Effects (backend preference + localized data)
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ApiConfiguration } from '../../api/api-configuration';
import { getPreferredLanguage } from '../../api/fn/i-18-n/get-preferred-language';
import { updatePreferredLanguage } from '../../api/fn/i-18-n/update-preferred-language';
import { LanguageService } from '../../i18n/language.service';
import { LanguageConfig, resolveSupportedLanguage } from '../../i18n/languages.config';
import { CountryActions } from '../country/country.actions';
import { selectCountriesLoaded } from '../country/country.selectors';
import { NotificationActions } from '../notification/notification.actions';
import { ProcessActions } from '../process/process.actions';
import { selectRiskMatrixState, selectRiskMeasures } from '../process/process.selectors';
import { ToastActions } from '../toast/toast.actions';
import { UserActions } from '../user/user.actions';
import { selectIsAuthenticated } from '../user/user.selectors';

/**
 * Keeps the language in sync with the user's account and refetches server-localized data.
 *
 * - After login the stored preference wins (it may have been changed on another device),
 *   unless the user picked a language on this device that the backend has not stored yet.
 * - When nothing was chosen on this device, the user saw the app in their browser language and
 *   kept it, so that language is stored on the account (a new user starts on the backend default).
 *   Known gap until the backend tells a chosen language from its default: an account set to
 *   English on another device is switched to the browser language on a new device.
 * - A language picked in the selector is stored on the account, so the backend uses it for
 *   emails and notifications.
 * - Countries, notifications and the open process's risk matrix are rendered by the backend,
 *   so they are refetched on a switch.
 */
@Injectable()
export class LanguageEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly store = inject(Store);
  private readonly languageService = inject(LanguageService);

  readonly syncOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initializeAuthSuccess),
      filter(({ isAuthenticated }) => isAuthenticated),
      switchMap(() =>
        this.languageService.hasUnsyncedPreference()
          ? this.savePreference(this.languageService.current())
          : this.applyStoredPreference(),
      ),
    ),
  );

  readonly saveUserChoice$ = createEffect(() =>
    this.languageService.changes$.pipe(
      filter(change => change.source === 'user'),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      // Logged out: the choice stays flagged as unsynced and is stored after the next login.
      filter(([, authenticated]) => authenticated),
      switchMap(([change]) => this.savePreference(change.language)),
    ),
  );

  readonly refetchLocalizedData$ = createEffect(() =>
    this.languageService.changes$.pipe(
      filter(change => change.source !== 'init'),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectCountriesLoaded),
        this.store.select(selectRiskMatrixState),
        this.store.select(selectRiskMeasures),
      ),
      switchMap(([, authenticated, countriesLoaded, riskMatrix, riskMeasures]) => [
        ...(countriesLoaded ? [CountryActions.reloadCountries()] : []),
        ...(authenticated ? [NotificationActions.refreshBell()] : []),
        ...(riskMatrix?.kycProcessId ? [ProcessActions.loadRiskMatrixState({ processId: riskMatrix.kycProcessId })] : []),
        ...(riskMeasures?.kycProcessId ? [ProcessActions.loadRiskMeasures({ processId: riskMeasures.kycProcessId })] : []),
      ]),
    ),
  );

  private applyStoredPreference(): Observable<Action> {
    return getPreferredLanguage(this.http, this.apiConfig.rootUrl).pipe(
      // Closest match, so a preference still stored as 'en' by an older backend resolves to en-US.
      map(response => resolveSupportedLanguage(response.body?.language)),
      filter((config): config is LanguageConfig => !!config),
      switchMap(stored => {
        const current = this.languageService.current();
        if (!this.languageService.hasSavedPreference()) {
          // Browser-detected language: store it on the account (automatic, so no toast on failure;
          // nothing is saved locally then, and the next login retries).
          if (stored.code === current.code) {
            this.languageService.markPreferenceSynced();
            return EMPTY;
          }
          return this.savePreference(current, { notifyOnFailure: false });
        }
        if (stored.code === current.code) return EMPTY;
        return from(this.languageService.setLanguage(stored.code, 'backend')).pipe(switchMap(() => EMPTY));
      }),
      // 404 (no app user for this login) or an older backend without the endpoint: keep the local language.
      catchError(() => EMPTY),
    );
  }

  private savePreference(language: LanguageConfig, { notifyOnFailure = true } = {}): Observable<Action> {
    return updatePreferredLanguage(this.http, this.apiConfig.rootUrl, { body: { language: language.code } }).pipe(
      switchMap(() => {
        this.languageService.markPreferenceSynced();
        return EMPTY;
      }),
      catchError(() => notifyOnFailure
        ? of(ToastActions.showToast({ message: 'LANGUAGE.SYNC_FAILED', notificationType: 'warning' }))
        : EMPTY),
    );
  }
}
